import * as Sentry from '@sentry/react'
import { requiredError } from 'Components/Forms/FormErrors'
import { AlertComponent } from 'Components/HelperComponents/AlertComponent'
import LoadingComponent from 'Components/HelperComponents/LoadingComponent'
import { EditComponent } from 'NewVersion/components/Fields/EditComponent'
import { fetchGuillotina } from 'NewVersion/services/guillotina'
import { useFormik } from 'formik'
import { useEdition } from 'hooks/useEdition'
import { useGuillotina } from 'hooks/useGuillotina'
import { useScrollToFirstError } from 'hooks/useScrollToFirstError'
import React, { Fragment, useEffect } from 'react'
import { useTranslation, withTranslation } from 'react-i18next'
import { get } from 'utils/objectUtils'
import { cleanObjectIds, getCurrentJWToken, getEmailFromToken } from 'utils/utils'
import Button from '../UI/Buttons/Button'

const Form = ({ representant, setArtista, ensTypeObj, toggleInit }) => {
  const { edition, currentEdition, loading } = useEdition()

  const {
    data: entitySchemaData,
    isLoading: entitySchemaLoading,
    error: entitySchemaError,
  } = useGuillotina({
    path: `@types/${ensTypeObj['interface']}`,
  })

  if (loading || entitySchemaLoading) {
    return <LoadingComponent />
  }

  return (
    <ArtistFormContent
      ensTypeObj={ensTypeObj}
      edition={edition}
      toggleInit={toggleInit}
      currentEdition={currentEdition}
      schema={{
        data: entitySchemaData,
        error: entitySchemaError,
        loading: entitySchemaLoading,
      }}
      setArtistaObject={setArtista}
      representant={representant}
    />
  )
}

const ArtistFormContent = ({
  ensTypeObj,
  edition,
  toggleInit,
  schema,
  setArtistaObject,
  representant,
  readOnlyForm = false,
}) => {
  const { t } = useTranslation()
  const [submitError, setSubmitError] = React.useState({
    hasError: false,
    message: '',
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleFormError = (error) => {
    const err = new Error(`Artist form error: ${JSON.stringify(get(error, 'info', error))}`)
    err.name = 'Artist form error'
    Sentry.captureException(err)
    if (error.response && error.response.status === 412) {
      if (get(error, 'info.code') === 'already-exists') {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('artist_title_already_exists')}`,
        })
      } else if (get(error, 'info.code') === 'invalid-email') {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('invalid_email')}`,
        })
      } else if (get(error, 'info.code') === 'promo-code-not-exist') {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('promo_code_not_exist')}`,
        })
      } else if (get(error, 'info.code') === 'different-representant') {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('artist_different_representant')}`,
        })
      } else {
        setSubmitError({
          hasError: true,
          message: `Error: ${t('error_create_or_update_ens')}`,
        })
      }
    } else {
      setSubmitError({
        hasError: true,
        message: `Error: ${t('error_generic')}`,
      })
    }
    window.scrollTo(0, 0)
  }

  const fetchCreatedArtist = (artistPath) => {
    const token = getCurrentJWToken()
    fetchGuillotina({
      path: artistPath,
      method: 'GET',
      token,
    })
      .then((response) => {
        setArtistaObject(response)
      })
      .catch((error) => {
        handleFormError(error)
      })
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true)
    const valuesEntity = {}
    const token = getCurrentJWToken()

    Object.keys(values).forEach((valueKey) => {
      if (valueKey.indexOf('entity_') === 0) {
        valuesEntity[valueKey.replace('entity_', '')] = values[valueKey] || null
      }
    })

    const data = Object.assign({}, valuesEntity, {
      '@type': ensTypeObj['interface'],
      tipus: 'artista',
      tarifa: 'gratuita',
    })

    if (representant) {
      data.representant = `/${cleanObjectIds(representant['@id'])}`
    }

    await fetchGuillotina({
      path: `${edition}`,
      method: 'POST',
      data: data,
      token: token,
    })
      .then((response) => {
        if (response) {
          const path = '/' + response['@id'].split('/').slice(-2).join('/')
          fetchCreatedArtist(path)
        }
      })
      .catch((error) => {
        handleFormError(error)
        setSubmitting(false)
        return
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const createInitialValue = (representantObj, copyTitle = false) => {
    return {
      entity_tarifa: 'gratuita',
      entity_tipus: 'artista',
      entity_representant: representantObj ? `/${cleanObjectIds(representantObj['@id'])}` : '',
      entity_title: representantObj && copyTitle ? representantObj.title : '',
      entity_nom_fiscal: representantObj ? representantObj.nom_fiscal : '',
      entity_nif: representantObj ? representantObj.nif : '',
      entity_adreca: representantObj ? representantObj.adreca : '',
      entity_codi_postal: representantObj ? representantObj.codi_postal : '',
      entity_poblacio: representantObj ? representantObj.poblacio : '',
      entity_pais: representantObj?.pais,
      entity_comunitat_autonoma: representantObj?.comunitat_autonoma,
      entity_comarca: representantObj?.comarca,
      entity_telefon: representantObj ? representantObj.telefon : '',
      entity_mobil: representantObj ? representantObj.mobil : '',
      entity_correu: representantObj ? representantObj.correu : getEmailFromToken(),
      entity_nom_contacte: representantObj ? representantObj.nom_contacte : '',
      entity_idioma: representantObj ? representantObj.idioma : '',
      entity_cognoms_contacte: representantObj ? representantObj.cognoms_contacte : '',
      entity_web: representantObj ? representantObj.web : '',
      entity_facebook: representantObj ? representantObj.facebook : '',
      entity_twitter: representantObj ? representantObj.twitter : '',
      entity_instagram: representantObj ? representantObj.instagram : '',
      entity_linkedin: representantObj ? representantObj.linkedin : '',
      entity_youtube: representantObj ? representantObj.youtube : '',
    }
  }

  const getInitialValues = React.useMemo(() => {
    const result = {}
    ensTypeObj['requiredFields'].forEach((item) => {
      result[`entity_${item}`] = undefined
    })
    const initialValue = createInitialValue(representant)
    return {
      ...result,
      ...initialValue,
    }
  }, [ensTypeObj])

  const formik = useFormik({
    initialValues: getInitialValues,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validate: (values) => {
      const errors = {}
      ensTypeObj['requiredFields'].forEach((key) => {
        if (key !== 'declaracio_acceptar') {
          const keyForm = `entity_${key}`
          let value = get(values, keyForm, null)
          if (typeof value === 'string') {
            value = value.trim()
          }
          if ((!(keyForm in values) || !value) && renderLocationFields(key)) {
            errors[keyForm] = requiredError()
          }
        }
      })

      const keyForm = `entity_correu`
      let value = get(values, keyForm, null)
      if (typeof value === 'string') {
        value = value.trim()
      }
      if (value && !value.match(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i)) {
        errors[keyForm] = t('invalid_email')
      }

      return errors
    },
  })
  useScrollToFirstError(formik.errors, formik.submitCount)

  const fieldsWithPrefixLabel = ['idioma', 'correu', 'telefon', 'mobil']

  const getLabel = (key) => {
    if (fieldsWithPrefixLabel.includes(key)) return `ens_${key}`
    return key
  }

  const renderLocationFields = (key) => {
    if (key === 'comunitat_autonoma' && get(formik, 'values.entity_pais', '') !== 'country-ES') {
      return false
    }
    if (
      key === 'comarca' &&
      (get(formik, 'values.entity_pais', '') !== 'country-ES' ||
        get(formik, 'values.entity_comunitat_autonoma', '') !== 'catalunya')
    ) {
      return false
    }
    return true
  }

  const renderFields = (key) => {
    if (key === 'declaracio_acceptar' || key === 'artista') return false
    return renderLocationFields(key)
  }

  const getDescription = (value, key) => {
    /*
    TODO: Refactor de les descripcions dels camps, evitar casos unics i utilitzar el type_name per crear les claus,
    es necessari modificar back per retornar la clau en el camp descripcio per exemple, analitzar-ho bé.
    */
    if (key === 'correu') {
      return t('ens_correu_description')
    } else if (key === 'web') {
      return t('web_description')
    } else if (key === 'facebook') {
      return t('facebook_description')
    } else if (key === 'twitter') {
      return t('twitter_description')
    } else if (key === 'instagram') {
      return t('instagram_description')
    } else if (key === 'linkedin') {
      return t('linkedin_description')
    } else if (key === 'youtube') {
      return t('youtube_description')
    }
    return value.description ? t(`${key}_description`) : null
  }

  const renderField = (key) => {
    if (renderFields(key) && get(schema, `data.properties[${key}]`, null)) {
      const value = schema.data.properties[key]
      const keyForm = `entity_${key}`
      return (
        <EditComponent
          key={keyForm}
          name={keyForm}
          label={key === 'title' ? t(getLabel(ensTypeObj['labelName'])) : t(getLabel(key))}
          schema={value}
          description={getDescription(value, key)}
          value={formik.values[keyForm]}
          required={(ensTypeObj['requiredFields'] ?? []).includes(key)}
          onBlur={() => {
            formik.setFieldTouched(keyForm)
          }}
          onChange={(ev) => {
            formik.setFieldValue(keyForm, ev)
            formik.validateForm()
          }}
          error={formik.touched[keyForm] && formik.errors[keyForm]}
          dataTest={`inputFieldTest_${keyForm}`}
        />
      )
    }
  }

  const renderColumns = (item) => {
    const middleIndex = Math.ceil(item['fields'].length / 2)
    return (
      <div className="fragment">
        <div className="fragmentColumn">
          {item['fields'].slice(0, middleIndex).map((key) => renderField(key))}
        </div>
        <div className="fragmentColumn">
          {item['fields'].slice(middleIndex).map((key) => renderField(key))}
        </div>
      </div>
    )
  }

  return (
    <>
      {submitError.hasError && submitError.message && (
        <AlertComponent type="danger"> {submitError.message}</AlertComponent>
      )}

      <h4>
        {representant
          ? t('create_new_artist_for_representant', { representant: representant.title })
          : `${t('create_new_artist_profile')} ${edition}`}
      </h4>

      <form
        className="Form"
        onSubmit={(values) => formik.handleSubmit(values)}
        data-test="formTest"
      >
        {schema &&
          schema.data &&
          !schema.loading &&
          ensTypeObj['orderFieldsForm']
            .filter((item) => item.title !== 'declaracio_fieldset')
            .map((item) => {
              return (
                <Fragment key={item['title']}>
                  {item['title'] !== 'entity_data' && <h5>{t(item['title'])}&nbsp;</h5>}
                  {renderColumns(item)}
                </Fragment>
              )
            })}

        <div>
          {formik.isSubmitting && <LoadingComponent />}
          {!readOnlyForm && (
            <div className="footer-buttons">
              <Button
                type="button"
                dataTest="btnCancelFormTest"
                disabled={formik.isSubmitting}
                onClick={() => toggleInit()}
                color="primaryOutlinedIcon outlined"
              >
                {t('cancel')}
              </Button>
              <Button type="submit" dataTest="btnSubmitFormTest" disabled={formik.isSubmitting}>
                {t('next')}
              </Button>
            </div>
          )}
        </div>
      </form>
    </>
  )
}

export const ArtistForm = withTranslation()(Form)
